<template>
  <!-- AGRUPADO: um controle só, e os destinos na lista. O ativador segue o idioma do vizinho —
       o gatilho da busca no xs —, e por isso é `size="small"` com `aria-label` e NENHUM tooltip:
       dois overlays no mesmo botão não se empilham, e num dedo o `mouseenter` vem sem o
       `mouseleave`, então o tooltip abriria junto com o menu e ficaria pendurado. -->
  <v-menu v-if="grouped" v-model="open" location="bottom end">
    <template #activator="{props}">
      <v-btn v-bind="props" variant="text" size="small" icon="mdi-dots-vertical"
             :aria-label="$tt('nav.goto')"/>
    </template>
    <!-- Dentro de um menu da barra: os subitens dele no lugar do resto, e uma linha no alto para
         voltar (escolha do usuário, contra um submenu flutuando ao lado). O `.stop` no clique das
         duas linhas de passagem é o que mantém o menu aberto: o clique que sobe ao conteúdo o fecha. -->
    <v-list v-if="inside" density="compact" min-width="200">
      <v-list-item prepend-icon="mdi-chevron-left" :title="inside.label" @click.stop="inside = null"/>
      <v-divider class="my-1"/>
      <ontos-menu-items :items="inside.items"/>
    </v-list>
    <v-list v-else density="compact" min-width="200">
      <!-- Os menus da barra que não couberam nela (o Configurar, no telefone), um por linha e
           fechados: entrar num deles mostra os subitens. -->
      <template v-if="menus.length">
        <v-list-item v-for="menu of menus" :key="menu.key" :prepend-icon="menu.icon" :title="menu.label"
                     append-icon="mdi-chevron-right" @click.stop="inside = menu"/>
        <v-divider class="my-1"/>
      </template>
      <template v-for="(destination, index) of destinations" :key="destination.key">
        <!-- O divisor antes da lista de ambientes: o que vem acima é "onde dentro deste
             ambiente" — o início dele —, e ela é sair. É o mesmo arranjo do seletor de ambiente,
             que termina igual. -->
        <v-divider v-if="last(index)" class="my-1"/>
        <v-list-item :to="destination.to" :prepend-icon="destination.icon"
                     :title="$tt(destination.label)"/>
      </template>
    </v-list>
  </v-menu>

  <!-- SOLTO: um botão por destino, com o tooltip explicando o ícone. É o desenho de sempre, e
       continua sendo o de qualquer largura a partir do sm. -->
  <template v-else>
    <v-btn v-for="destination of destinations" :key="destination.key"
           :to="destination.to" variant="text" icon exact>
      <v-icon>{{ destination.icon }}</v-icon>
      <v-tooltip activator="parent" location="bottom">{{ $tt(destination.label) }}</v-tooltip>
    </v-btn>
  </template>
</template>

<script lang="ts">
import {Component, Prop, Vue, Watch, toNative} from 'vue-facing-decorator'
import {EnhancedWorkpace} from '@/types/Workspace'
import {Destination, destinationsFor} from '@/utils/destinations'
import MenuItems, {MenuItem} from '@/components/shell/MenuItems.vue'

/** Um menu da barra que, sem lugar nela, vem para dentro deste: o ícone, o nome e os subitens. */
export type FoldedMenu = { key: string, icon: string, label: string, items: MenuItem[] }

/**
 * Para onde se pode ir daqui, na barra: o início do ambiente e a lista de ambientes.
 *
 * **Uma lista, duas formas.** A lista é do `destinationsFor`, que é pura; aqui só se decide se ela
 * sai como botões soltos ou dentro de um menu. Ter as duas formas lendo a MESMA lista é o que impede
 * uma de ganhar um destino que a outra não tem.
 *
 * **A regra de agrupar é uma só**: no `xs`, e havendo mais de um destino. Fora de um ambiente sobra
 * a lista de ambientes, um destino, e ela continua um botão, porque menu de um item é pior que o
 * botão que ele esconderia; dentro de um ambiente são dois, e agrupam.
 *
 * O aperto que fez isto existir: a 360px o grupo de ações pedia ~296px — com o selo de encerrado
 * são ~108px. E o conteúdo da barra é `overflow: hidden`: o que não cabe é cortado, não quebra linha.
 */
@Component({name: 'ontos-nav-destinations', components: {'ontos-menu-items': MenuItems}})
class NavDestinations extends Vue {
  /**
   * Os menus da barra que, no telefone, vêm para dentro deste — o Configurar, que na barra estreita
   * não cabia (pedido do usuário). Cada um é uma linha que se ABRE nos subitens dele, e não os
   * subitens soltos no meio dos destinos. Só valem agrupados: no sm para cima eles estão na barra.
   */
  @Prop({type: Array, default: () => []}) readonly menus!: FoldedMenu[]

  open = false
  /** O menu da barra em que se entrou, ou null no começo da lista. */
  inside: FoldedMenu | null = null

  /** Fechado, o menu volta ao começo: reabri-lo dentro do Configurar seria esquecer de onde se veio. */
  @Watch('open')
  onOpen(open: boolean) {
    if (!open) this.inside = null
  }

  /**
   * O ambiente, ou `null` quando não se está em nenhum.
   *
   * Anulável de propósito: fora de um ambiente o `App.vue` continua com o do anterior em mão, e
   * quem decide se há ambiente de verdade é ele, pelo `hasWorkspace`. Ver o `destinationsFor`.
   */
  @Prop({default: null}) readonly workspace!: EnhancedWorkpace | null

  get destinations(): Destination[] {
    return destinationsFor(this.workspace, this.$route.params)
  }

  get grouped(): boolean {
    return this.$vuetify.display.xs && this.destinations.length + this.menus.length > 1
  }

  /** O último destino é a lista de ambientes, e é dela que o divisor separa o resto. */
  last(index: number): boolean {
    return index > 0 && index === this.destinations.length - 1
  }
}

export default toNative(NavDestinations)
</script>
